/**
 * Audit API — durable mission-execution audit trail.
 *
 * Reads the persisted structural audit (shared.mission_audit on the
 * platform) via governance-service. This is the same endpoint admin-ui's
 * audit pages use: GET /v1/governance/audit/mission-execution/{executionId}.
 *
 * The trail is content-free by design (ADR-055): event types, tool/model
 * names, tokens, durations and outcomes are recorded; prompts, responses
 * and tool payloads stay on the Edge host. Chat-ui uses this as the
 * fallback source for the Activity panel when no live or chat-history
 * derived events are available (completed runs, platform-hosted missions).
 */

import { apiClient } from './api-client'

/** One persisted audit row — snake_case wire shape (no client-side case conversion in chat-ui). */
export interface MissionAuditEventRow {
  id: string
  created_at: string
  event_type: string
  node_id?: string | null
  agent_id?: string | null
  agent_name?: string | null
  user_display_name?: string | null
  hitl_request_id?: string | null
  tool_name?: string | null
  model_name?: string | null
  tokens_input?: number | null
  tokens_output?: number | null
  duration_ms?: number | null
  success?: boolean | null
  error_category?: string | null
  /** A mission_end row's terminal reason as the runner classified it (e.g. llm_deadline). */
  outcome?: string | null
  decision?: string | null
  side_effect?: string | null
  http_method?: string | null
  target_host?: string | null
  response_status?: number | null
}

/** governance-service wraps the payload in a `data` envelope:
 *  `{data: {count, execution_id, items: [...]}}` (verified live on dev). */
export interface MissionAuditEventsResponse {
  data?: {
    count?: number
    execution_id?: string
    items?: MissionAuditEventRow[]
  }
  /** Tolerated flat shape, in case the envelope is ever dropped */
  items?: MissionAuditEventRow[]
}

export const auditApi = {
  /**
   * Fetch the persisted audit trail for one execution.
   * Returns rows oldest-first regardless of backend ordering.
   */
  getExecutionAuditEvents: async (executionId: string): Promise<MissionAuditEventRow[]> => {
    const response = await apiClient.get<MissionAuditEventsResponse>(
      `/v1/governance/audit/mission-execution/${executionId}`,
    )
    const items = response?.data?.items ?? response?.items ?? []
    if (!Array.isArray(items)) return []
    return [...items].sort(
      (a, b) => Date.parse(a.created_at) - Date.parse(b.created_at),
    )
  },
}
